import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Form, Spinner, Table } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import AppPagination from "../../Components/AppPagination";
import PrintLoadSheet from "../../Components/Print/PrintLoadSheet";
import { getConfirmedOrders, updateBulkOrder, updateOrderStatus } from "../../Services/orderService";


const AllOrders = () => {
    const [orders, setOrders] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [bulkStatus, setBulkStatus] = useState("pending");
    const [loading, setLoading] = useState(false);

    const companyPin = useSelector(state => state.auth.companyPin);
    const base64Pin = btoa(companyPin);


    const [filters, setFilters] = useState({
        status: "all",
        from: "",
        to: "",
        search: "",
    });

    const STATUS_CONFIG = {
        delivered: { color: "#10b981", bg: "rgba(16,185,129,0.12)", label: "Delivered", icon: "✓" },
        pending: { color: "#f59e0b", bg: "rgba(245,158,11,0.12)", label: "Pending", icon: "⏳" },
        returned: { color: "#ef4444", bg: "rgba(239,68,68,0.12)", label: "Returned", icon: "↩" },
        cancelled: { color: "#6b7280", bg: "rgba(107,114,128,0.12)", label: "Cancelled", icon: "✕" },
    };

    const itemsPerPage = 50;

    // ✅ DATE FORMAT FIX
    const formatDateForAPI = (date, isEnd = false) => {
        if (!date) return undefined;

        const d = new Date(date);

        if (isEnd) {
            d.setHours(23, 59, 59, 999); // end of day fix
        } else {
            d.setHours(0, 0, 0, 0);
        }

        return d.toISOString();
    };

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);

            const params = {
                page,
                limit: itemsPerPage, // frontend se limit
                status: filters.status !== "all" ? filters.status : undefined,
                from: formatDateForAPI(filters.from),
                to: formatDateForAPI(filters.to, true),
                search: filters.search || undefined,
                company_pin: base64Pin
            };

            const res = await getConfirmedOrders(params);

            if (res?.status === "success") {
                setOrders(res.data || []);
                setTotalPages(res.totalPages || 1);
            } else {
                setOrders([]);
            }

        } catch (err) {
            console.error("Order fetch error:", err);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    }, [page, filters]);

    // ✅ Proper debounce
    useEffect(() => {
        const delay = setTimeout(() => {
            fetchOrders();
        }, 400);

        return () => clearTimeout(delay);
    }, [fetchOrders]);

    // Handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setPage(1);
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleStatusFilter = (value) => {
        setPage(1);
        setFilters(prev => ({ ...prev, status: value }));
    };

    const handleStatusChange = async (id, status) => {
        const res = await updateOrderStatus(id, status);
        if (res?.status === "success") {
            setOrders(prev =>
                prev.map(o => o.id === id ? { ...o, status } : o)
            );
        }
        fetchOrders()
    };

    const handleSelectRow = (trackingId) => {
        setSelectedOrders(prev =>
            prev.includes(trackingId)
                ? prev.filter(i => i !== trackingId)
                : [...prev, trackingId]
        );
    };

    const handleSelectAll = () => {
        const currentTrackingIds = orders.map(o => o.trackingId);

        const allSelected = currentTrackingIds.every(id =>
            selectedOrders.includes(id)
        );

        if (allSelected) {
            setSelectedOrders(prev =>
                prev.filter(id => !currentTrackingIds.includes(id))
            );
        } else {
            setSelectedOrders(prev => [
                ...new Set([...prev, ...currentTrackingIds])
            ]);
        }
    };

    const handleBulkUpdate = async () => {

        if (!bulkStatus) return;
        if (!selectedOrders.length) return;

        try {
            setLoading(true);

            const body = {
                trackingId: selectedOrders, // ✅ correct
                status: bulkStatus
            };

            const response = await updateBulkOrder(body);

            console.log("API Response:", response);

            if (response?.success) {

                // ✅ FIX: trackingId se match karo
                setOrders(prev =>
                    prev.map(o =>
                        selectedOrders.includes(o.trackingId)
                            ? { ...o, status: bulkStatus }
                            : o
                    )
                );

                setSelectedOrders([]);
                setBulkStatus("pending");

            } else {
                console.error(response?.message || "Bulk update failed");
            }

        } catch (error) {
            console.error("Bulk update failed:", error);
        } finally {
            setLoading(false);
        }
    };

    const getBadge = (status) => {
        if (status === "pending") return "primary";
        if (status === "delivered") return "success";
        if (status === "returned") return "danger";
        return "secondary";
    };

    return (
        <div className="container pb-5">
            <div className="row">

                <div className="col">
                    <h4 className="mb-3">Update Orders</h4>
                </div>


                <div className="col-auto">
                    <Link to="/portal/bulk-update"
                        size="sm"
                        as="button"
                        className="btn btn-dark"
                        style={{ fontSize: "13px" }}>
                        Bulk Update
                    </Link>
                    {/* <button className="oms-btn oms-btn-ghost ">Bulk Update</button> */}
                </div>

            </div>

            {
                selectedOrders.length > 0 && (
                    <div className="d-flex gap-2 mb-3">
                        <Form.Select
                            style={{ width: 200, fontSize: "13px" }}
                            className="oms-select-dark"
                            value={bulkStatus}
                            onChange={(e) => setBulkStatus(e.target.value)}
                        >
                            <option value="pending">Pending</option>
                            <option value="delivered">Delivered</option>
                            <option value="returned">Returned</option>
                        </Form.Select>

                        <Button variant="dark"
                            className=""
                            style={{ fontSize: "13px" }}
                            onClick={handleBulkUpdate}>
                            Update Selected ({selectedOrders.length})
                        </Button>
                    </div>
                )
            }

            {/* Filters */}
            <div className="row g-2 mb-3 align-items-end justify-content-end">

                <div className="col-auto">
                    <Form.Label className="oms-label-dark">Status</Form.Label><br />
                    <div className="oms-filters">

                        {["all", "pending", "delivered", "returned"].map((s) => (
                            <div
                                key={s}
                                className={`oms-filter-chip ${filters.status === s ? "active" : ""
                                    }`}
                                onClick={() => handleStatusFilter(s)}
                            >
                                {s.charAt(0).toUpperCase() + s.slice(1)}

                                {/* <span style={{ marginLeft: 6, opacity: 0.6 }}>
                                    {s === "all"
                                        ? orders.length
                                        : orders.filter((o) => o.status === s).length}
                                </span> */}
                            </div>
                        ))}
                    </div>

                </div>


                <div className="col-auto">
                    <Form.Label className="oms-label-dark">From</Form.Label>
                    <Form.Control
                        type="date"
                        name="from"
                        className="oms-input-dark"
                        value={filters.from}
                        onChange={handleFilterChange}
                    />
                </div>

                <div className="col-auto">
                    <Form.Label className="oms-label-dark">To</Form.Label>
                    <Form.Control
                        type="date"
                        name="to"
                        className="oms-input-dark"
                        value={filters.to}
                        onChange={handleFilterChange}
                    />
                </div>


                <div className="col-auto">
                    <Form.Label className='oms-label-dark'>Search</Form.Label>

                    <div className="oms-search">
                        <input
                            type="text"
                            name="search"
                            placeholder="Search"
                            value={filters.search}
                            onChange={handleFilterChange}
                            disabled={loading} />

                        <span style={{ color: "var(--text-muted)", fontSize: 14 }}>
                            <FaSearch />
                        </span>
                    </div>

                </div>


                <div className="col-auto ">
                    <PrintLoadSheet title={"Pending Orders"} orders={orders} itemsPerPage={itemsPerPage} page={page} />
                </div>
            </div>

            <Table bordered responsive className="d-none">
                <thead>
                    <tr>
                        <th>
                            <Form.Check
                                type="checkbox"
                                checked={
                                    orders.length > 0 &&
                                    orders.every(o => selectedOrders.includes(o.trackingId))
                                }
                                onChange={handleSelectAll}
                            />
                        </th>
                        <th>#</th>
                        <th>Order ID</th>
                        <th>VPP</th>
                        <th>Name</th>
                        <th>Contact</th>
                        <th>City</th>
                        <th>Product</th>
                        <th>Qty</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Update</th>
                    </tr>
                </thead>

                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan="12" className="text-center">
                                <Spinner animation="border" size="sm" /> Loading...
                            </td>
                        </tr>
                    ) : orders.length ? (
                        orders.map((o, i) => (
                            <tr key={o.id}>
                                <td>
                                    <Form.Check
                                        type="checkbox"
                                        checked={selectedOrders.includes(o.trackingId)}
                                        onChange={() => handleSelectRow(o.trackingId)}
                                    />
                                </td>
                                <td>{i + 1 + (page - 1) * itemsPerPage}</td>
                                <td>{o.trackingId}</td>
                                <td>{o.VppNumber || "-"}</td>
                                <td>{o.customerName}</td>
                                <td>{o.phoneNumber}</td>
                                <td>{o.city}</td>
                                <td>{o.product || "-"}</td>
                                <td>{o.quantity}</td>
                                <td>{o.amount}</td>
                                <td>
                                    <Badge bg={getBadge(o.status)}>
                                        {o.status}
                                    </Badge>
                                </td>
                                <td>
                                    <Form.Select
                                        size="sm"
                                        value={o.status}
                                        onChange={(e) =>
                                            handleStatusChange(o.trackingId, e.target.value)
                                        }
                                    >
                                        <option value="pending">Pending</option>
                                        <option value="delivered">Delivered</option>
                                        <option value="returned">Returned</option>
                                    </Form.Select>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="12" className="text-center">
                                No orders found
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>

            <div className="oms-table-wrap">
                <div className="oms-table-header">
                    <div>
                        <span className="oms-table-title">Order Records</span>
                        <span className="oms-table-count">{orders.length} orders</span>
                    </div>
                </div>
                {orders.length === 0 ? (
                    <div className="oms-empty">
                        <div className="oms-empty-icon">🔍</div>
                        <div className="oms-empty-text">No orders found</div>
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table>
                            <thead>
                                <tr>
                                    <th>
                                        <Form.Check
                                            type="checkbox"
                                            checked={
                                                orders.length > 0 &&
                                                orders.every(o => selectedOrders.includes(o.trackingId))
                                            }
                                            onChange={handleSelectAll}
                                        />
                                    </th>
                                    <th>#</th>
                                    <th>Order Id</th>
                                    <th>Tracking</th>
                                    <th>Customer</th>
                                    <th>Contact</th>
                                    <th>Address</th>
                                    <th>Product</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                    {/* <th>Date</th> */}
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map(order => {
                                    const st = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                                    return (
                                        <tr key={order.id}>
                                            <td>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={selectedOrders.includes(order.trackingId)}
                                                    onChange={() => handleSelectRow(order.trackingId)}
                                                />
                                            </td>
                                            <td>{orders.indexOf(order) + 1 + (page - 1) * itemsPerPage}</td>
                                            <td>
                                                <div style={{ fontSize: "14px" }} className="oms-track-id">{order.trackingId}</div>
                                            </td>
                                            <td>
                                                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{order.VppNumber || <span style={{ opacity: 0.4 }}>—</span>}</div>
                                            </td>

                                            <td>
                                                <div className="oms-customer" style={{ textTransform: "capitalize" }}>{order.customerName}</div>
                                                {/* {order.remark && <div style={{ fontSize: 11, color: "var(--gold)", marginTop: 2 }}>★ {order.remark}</div>} */}
                                            </td>
                                            <td>
                                                <div className="oms-phone" style={{ whiteSpace: "pre" }}>{order.phoneNumber?.replace(/\r\n/g, "\n")}</div>
                                            </td>
                                            <td><div className="oms-address" title={order.address}>{order.city ? order.city : "-"}</div></td>
                                            <td>
                                                <div style={{ fontSize: 12, color: "white", textTransform: "capitalize" }}>{order.product}</div>
                                            </td>
                                            <td><div className="oms-amount" style={{ color: "var(--green)" }}>₨{parseInt(order.amount).toLocaleString()}</div></td>

                                            <td>
                                                <span className="oms-badge" style={{ background: st.bg, color: st.color }}>
                                                    {st.icon} {st.label}
                                                </span>
                                            </td>
                                            {/* <td><div style={{ fontSize: 12, color: "var(--text-muted)" }}>{new Date(order.createdAt).toLocaleDateString()}</div></td> */}
                                            <td>
                                                <td>
                                                    <Form.Select
                                                        size="sm"
                                                        className="oms-select-dark"
                                                        value={order.status}
                                                        onChange={(e) =>
                                                            handleStatusChange(order.trackingId, e.target.value)
                                                        }
                                                    >
                                                        <option value="pending">Pending</option>
                                                        <option value="delivered">Delivered</option>
                                                        <option value="returned">Returned</option>
                                                    </Form.Select>
                                                </td>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                <div className="oms-table-footer ">
                    <AppPagination
                        page={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                    />
                </div>
            </div>


        </div >
    );
};

export default AllOrders;